import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { getEvent, createEvent, updateEvent } from '../lib/api-client';
import type { Event } from '../types';

const AdminContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  display: 'flex',
  justifyContent: 'center',
}));

const AdminCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  width: '100%',
  maxWidth: 600,
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

export const EventDetailsAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const isNewEvent = eventId === 'new';

  const [loading, setLoading] = useState(!isNewEvent);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    venueName: '',
    date: '',
    registrationDeadline: '',
    website: '',
    summary: '',
    description: '',
    isActive: true,
    eventImage: '',
  });

  useEffect(() => {
    if (!isNewEvent && eventId) {
      const fetchEventData = async () => {
        try {
          setLoading(true);
          const event = await getEvent(eventId);
          setFormData({
            name: event.name || '',
            location: event.location || '',
            venueName: event.venueName || '',
            date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
            registrationDeadline: event.registrationDeadline
              ? new Date(event.registrationDeadline).toISOString().split('T')[0]
              : '',
            website: event.website || '',
            summary: event.summary || '',
            description: event.description || '',
            isActive: event.isActive !== undefined ? event.isActive : true,
            eventImage: event.eventImage || '',
          });
          setError(null);
        } catch (err: any) {
          console.error('Failed to fetch event:', err);
          setError(err.message || 'Failed to load event');
        } finally {
          setLoading(false);
        }
      };

      fetchEventData();
    }
  }, [eventId, isNewEvent]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.location || !formData.date) {
      setSaveError('Please fill in all required fields (Name, Location, Date)');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      const eventData = {
        ...formData,
        date: new Date(formData.date),
        registrationDeadline: formData.registrationDeadline
          ? new Date(formData.registrationDeadline)
          : undefined,
        registrationPrices: [],
        attendees: [],
        competitions: [],
        updateHistory: [],
      };

      if (isNewEvent) {
        await createEvent(eventData);
      } else if (eventId) {
        await updateEvent(eventId, eventData);
      }

      navigate('/events-admin');
    } catch (err: any) {
      console.error('Failed to save event:', err);
      setSaveError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/events-admin');
  };

  if (loading) {
    return (
      <AdminContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminCard>
        <HeaderBox>
          <Typography variant="h4" component="h1" sx={{ textAlign: 'center' }}>
            {isNewEvent ? 'Create New Event' : 'Edit Event'}
          </Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} size="small">
            Back to Events
          </Button>
        </HeaderBox>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Grid container spacing={3}>
            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Event Name"
                required
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={saving}
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Location"
                required
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                disabled={saving}
                placeholder="e.g., Los Angeles, CA"
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Venue Name"
                value={formData.venueName}
                onChange={(e) => handleChange('venueName', e.target.value)}
                disabled={saving}
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Event Date"
                type="date"
                required
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Registration Deadline"
                type="date"
                value={formData.registrationDeadline}
                onChange={(e) => handleChange('registrationDeadline', e.target.value)}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                disabled={saving}
                placeholder="https://example.com"
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Event Image URL"
                value={formData.eventImage}
                onChange={(e) => handleChange('eventImage', e.target.value)}
                disabled={saving}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Summary"
                value={formData.summary}
                onChange={(e) => handleChange('summary', e.target.value)}
                disabled={saving}
                placeholder="Brief description for list views"
                multiline
                rows={2}
              />
            </Grid>

            <Grid container spacing={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={saving}
                placeholder="Full event description"
                multiline
                rows={6}
              />
            </Grid>

            <Grid container spacing={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Event is Active"
                sx={{ display: 'flex', justifyContent: 'center' }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 2 }}
          >
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save Event'}
            </Button>
            <Button onClick={handleBack} disabled={saving} fullWidth>
              Cancel
            </Button>
          </Box>
        </Box>
      </AdminCard>
    </AdminContainer>
  );
};
